<template>
  <CCard class="mb-3">
    <CCardHeader>
      <h5 class="my-2">
        <CIcon name="cilInfo" class="mr-2"/>
        <translate translate-context="edge-orders.summary">Order Details</translate>
      </h5>
    </CCardHeader>

    <CCardBody class="px-0">
      <CContainer fluid>
        <dl class="row mb-0 list-striped">
          <!-- Order Number -->
          <template v-if="hasPerm('edge.orders.order_number')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Order Number</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              <strong>{{ order.order_number }}</strong>
            </dd>
          </template>

          <!-- Transaction Number -->
          <template v-if="hasPerm('edge.orders.transaction_number')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Transaction Number</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              {{ order.transaction_number }}
            </dd>
          </template>

          <!-- Cabin -->
          <template v-if="hasPerm('edge.orders.cabin')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Cabin</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              {{ order.cabin_id }}
            </dd>
          </template>

          <!-- Guest -->
          <template v-if="hasPerm('edge.orders.guest')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Guest</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              {{ order.guest_id }}
            </dd>
          </template>

          <!-- Badge -->
          <template v-if="hasPerm('edge.orders.badge')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Badge</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              {{ order.badge_id || '-' }}
            </dd>
          </template>

          <!-- PicaCode (with browse photo link) -->
          <template v-if="order.picacodes && order.picacodes.length > 0">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">PicaCode</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              <div v-for="(code, index) in order.picacodes" :key="index" class="mb-1">
                <span v-if="!canBrowsePhoto">{{ code }}</span>
                <router-link
                  v-else
                  :to="{
                    name: 'photoSearch.search',
                    params: { picaServiceId: picaServiceId },
                    query: { pica_code: code }
                  }"
                  v-c-tooltip="{content: $pgettext('edge-orders.summary', 'Browse Photos')}"
                >
                  <CIcon name="cipSearch" class="mr-2"/>
                  {{ code }}
                </router-link>
              </div>
            </dd>
          </template>

          <!-- Status -->
          <template v-if="hasPerm('edge.orders.status')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Status</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              <CBadge class="badge-outline" shape="pill" :color="statusBadgeColor">
                <div class="d-inline-flex align-items-center py-1 px-2">
                  <CIcon name="cipDot" :class="`text-${statusColor}`" size="custom-size" :height="8"/>
                  <span class="ml-2 font-weight-medium">{{ statusLabel }}</span>
                </div>
              </CBadge>
            </dd>
          </template>

          <!-- Created At -->
          <template v-if="hasPerm('edge.orders.created_at')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Created At</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              {{ order.created_at | formatDate('DD/MM/YYYY HH:mm') }}
            </dd>
          </template>

          <!-- Total Amount (with permission) -->
          <template v-if="hasPerm('edge.orders.total')">
            <dt class="col-sm-5 col-xl-4">
              <translate translate-context="edge-orders.summary">Total Amount</translate>
            </dt>
            <dd class="col-sm-7 col-xl-8">
              <strong class="text-primary">{{ order.total_amount | currency(order.currency) }}</strong>
            </dd>
          </template>
        </dl>
      </CContainer>
    </CCardBody>
  </CCard>
</template>

<script>
import { mapGetters } from 'vuex'
import { getOrderStatusLabel, getOrderStatusColor } from '@/domain/core/constant/orderStatus'
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'OrderSummary',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters('edgeOrders', [
      'picaServiceId',
      'orderItems'
    ]),
    ...mapGetters('eventWizard', [
      'canSeePhotoSearch'
    ]),

    canBrowsePhoto() {
      return this.canSeePhotoSearch && this.hasPerm('photo_search.filter_by_pica_code')
    },

    statusLabel() {
      return getOrderStatusLabel(this.order.status)
    },

    statusColor() {
      return getOrderStatusColor(this.order.status)
    },

    statusBadgeColor() {
      const color = this.statusColor
      const badgeColorMap = {
        'primary': 'light',
        'success': 'light',
        'warning': 'light',
        'danger': 'light',
        'secondary': 'light',
        'dark': 'light'
      }
      return badgeColorMap[color] || 'light'
    }
  },
  filters: {
    currency: Filters.currency,
    formatDate: Filters.formatDate
  }
}
</script>

<style scoped>
.list-striped dt,
.list-striped dd {
  padding: 0.75rem 1rem;
}

.list-striped dt:nth-of-type(odd),
.list-striped dd:nth-of-type(odd) {
  background-color: #f8f9fa;
}

.font-weight-medium {
  font-weight: 500;
}
</style>
